import { getSupabaseClient } from '../../lib/supabase'

export type VisualEstimateRequestDraft = {
  id: string
  organization_id: string
  property_id: string
  category_id: string
  desired_outcome: string
  measurements: string | null
  quality_tier: 'economy' | 'standard' | 'premium'
  postal_code: string
  scope_items: string[]
  assumptions: string[]
  price_factors: string[]
  total_min_cents: number | null
  total_max_cents: number | null
  duration_min_days: number | null
  duration_max_days: number | null
  confidence: 'low' | 'medium' | 'high' | null
  disclaimer: string | null
  service_request_id: string | null
  service_categories: { name: string } | null
  properties: {
    name: string
    city: string
    region: string
    postal_code: string
  } | null
  marketplace_visual_estimate_images: Array<{
    id: string
    file_name: string
    storage_path: string
  }>
}

export async function getVisualEstimateRequestDraft(estimateId: string): Promise<VisualEstimateRequestDraft> {
  const { data, error } = await getSupabaseClient()
    .from('marketplace_visual_estimates' as never)
    .select('id,organization_id,property_id,category_id,desired_outcome,measurements,quality_tier,postal_code,scope_items,assumptions,price_factors,total_min_cents,total_max_cents,duration_min_days,duration_max_days,confidence,disclaimer,service_request_id,service_categories(name),properties(name,city,region,postal_code),marketplace_visual_estimate_images(id,file_name,storage_path)')
    .eq('id', estimateId)
    .eq('status', 'completed')
    .single() as { data: VisualEstimateRequestDraft | null; error: Error | null }

  if (error || !data) throw error ?? new Error('Completed visual estimate not found.')
  return data
}

export function buildVisualEstimateDescription(draft: VisualEstimateRequestDraft) {
  const sections = [
    draft.desired_outcome.trim(),
    draft.measurements?.trim() ? `Measurements/details supplied: ${draft.measurements.trim()}` : '',
    draft.scope_items.length ? `AMIGO visible scope:\n${draft.scope_items.map((item) => `• ${item}`).join('\n')}` : '',
    draft.assumptions.length ? `Planning assumptions:\n${draft.assumptions.map((item) => `• ${item}`).join('\n')}` : '',
    `AMIGO Visual Estimate™ reference: ${draft.id}`,
    draft.disclaimer ?? 'Preliminary AI estimate only. Provider must confirm final scope and price.',
  ]
  return sections.filter(Boolean).join('\n\n')
}

export function getTransferredEstimatePricing(
  draft: Pick<VisualEstimateRequestDraft, 'total_min_cents' | 'total_max_cents'>,
) {
  return {
    budget_min_cents: draft.total_min_cents,
    budget_max_cents: draft.total_max_cents,
  }
}

export async function publishRequestFromVisualEstimate(input: {
  draft: VisualEstimateRequestDraft
  userId: string
  title: string
  description: string
  city: string
  region: string
  postalCode: string
  desiredOn: string | null
}) {
  const supabase = getSupabaseClient()
  if (input.draft.service_request_id) throw new Error('This estimate has already been published to the marketplace.')

  const { data: request, error: requestError } = await supabase
    .from('service_requests')
    .insert({
      organization_id: input.draft.organization_id,
      property_id: input.draft.property_id,
      category_id: input.draft.category_id,
      title: input.title.trim(),
      description: input.description.trim(),
      city: input.city.trim(),
      region: input.region.trim(),
      postal_code: input.postalCode.trim(),
      // These values remain internal pricing intelligence; the requester
      // cannot edit or see them during the transfer.
      ...getTransferredEstimatePricing(input.draft),
      desired_service_on: input.desiredOn || null,
      status: 'published',
      created_by: input.userId,
    })
    .select('id')
    .single()

  if (requestError || !request) throw requestError ?? new Error('Unable to publish marketplace request.')

  const { error: linkError } = await supabase
    .from('marketplace_visual_estimates' as never)
    .update({ service_request_id: request.id, updated_at: new Date().toISOString() } as never)
    .eq('id', input.draft.id)
    .is('service_request_id', null)

  if (linkError) {
    await supabase.from('service_requests').delete().eq('id', request.id)
    throw linkError
  }

  return request.id
}
